import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from "react-router-dom";
import { Box, FormControlLabel, Switch, Grid, TextField, Typography, Divider, Link as MuiLink, Autocomplete, CircularProgress, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { canEditField } from '../hartUtils';

// INITIAL FORM DATA - Default values for creating a new session
export const sessionInitialData = {
  session_id: '',
  job_no: '',
  job_name: '',
  sess_start: null,
  sess_end: null,
  sess_type: '',
  mode: '',
  sample_type: '',
  sample_geography: '',
  sample_n: '',
  sess_notes: '',
  coded_type: '',
  coded_by: '',
  code_notes: '',
  // Survey fields
  issue_type: '',
  survey_length: '',
  geo_type: '',
  vf_match: false,
  // Focus Group fields
  fg_type: '',
  fg_geography: '',
  fg_topic: '',
  moderator: '',
  recruited: '',
};

// FORM VALIDATION
export function validateSession(data, isCreating = false) {
  const errors = {};

  // Job required
  if (isCreating && !data.job_no) {
    errors.job_no = 'Please select a job';
  }


  // Validate survey_length if provided (should be a positive number)
  if (data.survey_length) {
    const length = parseInt(data.survey_length, 10);
    if (isNaN(length) || length < 0) {
      errors.survey_length = 'Please enter a valid number of minutes';
    }
  }

  // Validate recruited if provided (should be a positive number)
  if (data.recruited) {
    const recruited = parseInt(data.recruited, 10);
    if (isNaN(recruited) || recruited < 0) {
      errors.recruited = 'Please enter a valid number';
    }
  }

  // Validate sample_n if provided (should be a positive number)
  if (data.sample_n) {
    const sampleN = parseInt(data.sample_n, 10);
    if (isNaN(sampleN) || sampleN < 0) {
      errors.sample_n = 'Please enter a valid number';
    }
  }

  return errors;
}

// NULLABLE FIELDS - Fields that should be converted from empty string to null
export const sessionNullableFields = [
  'sess_start',
  'sess_end',
  'sess_type',
  'mode',
  'sample_type',
  'sample_geography',
  'sample_n',
  'sess_notes',
  'coded_type',
  'coded_by',
  'code_notes',
  'issue_type',
  'survey_length',
  'geo_type',
  'fg_type',
  'fg_geography',
  'fg_topic',
  'moderator',
  'recruited',
];

// Helper function to format dates for display in Eastern Time (view mode)
const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }) + ' ET';
  } catch {
    return dateString;
  }
};

// Helper function to format datetime for input fields in Eastern Time (edit mode)
// API values have timezone (Z or offset) — convert those to Eastern for display.
// Naive values (YYYY-MM-DDTHH:mm from picker) — pass through as-is; form expects Eastern.
const formatDateTimeForInput = (dateString) => {
  if (!dateString) return '';
  try {
    const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(dateString);
    if (!hasTimezone) {
      // Already Eastern (from API display or from our onChange conversion)
      const m = dateString.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?::\d{2})?/);
      return m ? m[1] : dateString;
    }
    const date = new Date(dateString);
    // Return YYYY-MM-DDTHH:MM format for datetime-local input
    const etString = date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const [datePart, timePart] = etString.split(', ');
    const [month, day, year] = datePart.split('/');
    return `${year}-${month}-${day}T${timePart}`;
  } catch {
    return dateString;
  }
};

// DRF with TIME_ZONE=UTC treats naive "2026-03-18T13:00" as 1pm UTC (= 9am Eastern).
// Append Eastern offset so backend parses as 1pm Eastern.
const toEasternISO = (naiveEastern) => {
  if (!naiveEastern) return null;
  const m = naiveEastern.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return naiveEastern;
  const [, year, month, day] = m;
  const utcNoon = new Date(Date.UTC(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10), 12, 0, 0));
  const etHour = parseInt(utcNoon.toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false }), 10);
  const offset = 12 - etHour; // 4 for EDT, 5 for EST
  const offsetStr = `-${String(offset).padStart(2, '0')}:00`;
  return `${naiveEastern.replace(/T(\d{2}):(\d{2})(?::\d{2})?/, 'T$1:$2:00')}${offsetStr}`;
};

// Simple FormField that handles both view and edit modes
const FormField = ({ label, value, editable, onChange, error, helperText, required, type, multiline, minRows, maxRows, formatValue, linkTo, isEmail, min, max }) => {
  if (!editable) {
    // View mode
    const displayValue = formatValue ? formatValue(value) : (value || '—');

    if (multiline) {
      return (
        <>
          <Typography variant="body2" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {displayValue && displayValue !== '—' && String(displayValue).trim() ? displayValue : '—'}
          </Typography>
        </>
      );
    }

    // Render value as link (internal route)
    if (linkTo && displayValue !== '—') {
      return (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mb: 0.5 }}>
            {label}
          </Typography>
          <MuiLink
            component={Link}
            to={linkTo}
            underline="always"
            sx={{
              color: 'secondary.main',
              textDecorationColor: (theme) => theme.palette.secondary.main,
              '&:hover': {
                color: 'primary.main',
                textDecorationColor: (theme) => theme.palette.primary.main,
              }
            }}
          >
            {displayValue}
          </MuiLink>
        </Box>
      );
    }

    // Render value as mailto link
    if (isEmail && displayValue !== '—') {
      return (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mb: 0.5 }}>
            {label}
          </Typography>
          <MuiLink
            href={`mailto:${displayValue}`}
            underline="always"
            sx={{
              color: 'secondary.main',
              textDecorationColor: (theme) => theme.palette.secondary.main,
              '&:hover': {
                color: 'primary.main',
                textDecorationColor: (theme) => theme.palette.primary.main,
              }
            }}
          >
            {displayValue}
          </MuiLink>
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.primary' }}>
          {displayValue}
        </Typography>
      </Box>
    );
  }

  // Edit mode - format datetime values for input fields
  const inputValue = type === 'datetime-local' ? formatDateTimeForInput(value) : (value || '');

  return (
    <TextField
      label={label}
      value={inputValue}
      fullWidth
      required={required}
      type={type}
      multiline={multiline}
      minRows={minRows}
      maxRows={maxRows}
      onChange={onChange}
      variant="outlined"
      error={!!error}
      helperText={error || helperText || ''}
      InputLabelProps={{
        shrink: type === 'datetime-local' ? true : undefined,
        sx: { color: 'primary.main', fontWeight: 600 }
      }}
      inputProps={{
        min: min,
        max: max,
      }}
    />
  );
};

const SessionFormFields = ({ data, editable, onChange, errors = {}, displayName, projectId, isCreating = false }) => {
  const gridSpacing = editable ? 3 : 4;
  const gridBreakpoint = editable ? 'sm' : 'md';
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  const fieldEditable = (fieldName) => {
    return editable && canEditField(fieldName, 'session', projectId);
  };
  
  // State for dropdown options
  const [sessionTypes, setSessionTypes] = useState([]);
  const [surveyModes, setSurveyModes] = useState([]);
  const [fgTypes, setFgTypes] = useState([]);
  const [geographyTypes, setGeographyTypes] = useState([]);

  useEffect(() => {
    if (isCreating) {
      setLoadingJobs(true);
      fetch('/hart/jobs', {
        credentials: 'include',
      })
        .then(res => res.json())
        .then(data => {
          setJobs(data.results || data || []);
          setLoadingJobs(false);
        })
        .catch(err => {
          console.error('Error fetching jobs:', err);
          setLoadingJobs(false);
        });
    }
  }, [isCreating]);
  
  // Fetch dropdown values on mount
  useEffect(() => {
    const fetchDropdownValues = async () => {
      try {
        const [sessionTypesRes, modesRes, fgTypesRes, geographyTypesRes] = await Promise.all([
          fetch('/hart/session_types', { credentials: 'include' }),
          fetch('/hart/survey_modes', { credentials: 'include' }),
          fetch('/hart/fg_types', { credentials: 'include' }),
          fetch('/hart/geography_types', { credentials: 'include' }),
        ]);
        
        if (sessionTypesRes.ok) setSessionTypes(await sessionTypesRes.json() || []);
        if (modesRes.ok) setSurveyModes(await modesRes.json() || []);
        if (fgTypesRes.ok) setFgTypes(await fgTypesRes.json() || []);
        if (geographyTypesRes.ok) setGeographyTypes(await geographyTypesRes.json() || []);
      } catch (error) {
        console.error('Error fetching dropdown values:', error);
      }
    };
    
    fetchDropdownValues();
  }, []);
  
  // Helper to find matching value (case-insensitive)
  const findMatchingValue = (value, options) => {
    if (!value || !options) return '';
    const match = options.find(
      opt => opt.name.toLowerCase() === value.toLowerCase()
    );
    return match ? match.name : '';
  };

  const jobDetailUrl = data.job_no ? `/projects/${projectId}/hart/jobs/${data.job_no}` : null;
  const selectedJob = jobs.find(job => job.job_no === data.job_no) || null;
  const fieldsDisabled = isCreating && !data.job_no;

  return (
    <Box component={editable ? "form" : "div"} sx={{ mt: 0, mb: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h2" sx={{ mb: 0 }}>
          {displayName} Details
        </Typography>
      </Box>

      <Grid container spacing={gridSpacing}>
        {/* ===== SESSION DETAILS SECTION ===== */}

        {/* Row 1: Job Selection */}
        {isCreating ? (
          <Grid item xs={12}>
            <Autocomplete
              options={jobs}
              loading={loadingJobs}
              value={selectedJob}
              onChange={(event, newValue) => {
                onChange({
                  ...data,
                  job_no: newValue?.job_no || '',
                  job_name: newValue?.job_name || '',
                });
              }}
              getOptionLabel={(option) => `${option.job_no} - ${option.job_name}`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Job"
                  required
                  error={!!errors.job_no}
                  helperText={errors.job_no || 'Session must be tied to an existing job'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingJobs ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  InputLabelProps={{
                    sx: { color: 'primary.main', fontWeight: 600 }
                  }}
                />
              )}
            />
          </Grid>
        ) : (
          <>
            {/* Row 1: Job No | Job Name */}
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              <FormField
                label="Job No"
                value={data.job_no}
                editable={false}
                onChange={() => { }}
                linkTo={jobDetailUrl}
              />
            </Grid>
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              <FormField
                label="Job Name"
                value={data.job_name}
                editable={false}
                onChange={() => { }}
                linkTo={jobDetailUrl}
              />
            </Grid>
          </>
        )}

        {/* Wrapper to disable all fields when no job selected */}
        <Grid item xs={12} sx={{
          opacity: fieldsDisabled ? 0.5 : 1,
          pointerEvents: fieldsDisabled ? 'none' : 'auto',
          userSelect: fieldsDisabled ? 'none' : 'auto',
        }}>
          <Grid container spacing={gridSpacing}>
            {/* Row 2: Session ID | Session Type */}
            {!isCreating && (
              <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
                <FormField
                  label="Session ID"
                  value={data.session_id}
                  editable={false}
                  onChange={() => {}}
                  helperText="System generated"
                />
              </Grid>
            )}
            <Grid item xs={12} {...{ [gridBreakpoint]: isCreating ? 12 : 6 }}>
              {fieldEditable('sess_type') ? (
                <FormControl fullWidth error={!!errors.sess_type}>
                  <InputLabel sx={{ color: 'primary.main', fontWeight: 600 }}>Session Type</InputLabel>
                  <Select
                    value={findMatchingValue(data.sess_type, sessionTypes) || ''}
                    onChange={(e) => onChange({ ...data, sess_type: e.target.value })}
                    label="Session Type"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {sessionTypes.map((type) => (
                      <MenuItem key={type.name} value={type.name}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.sess_type && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.sess_type}
                    </Typography>
                  )}
                </FormControl>
              ) : (
                <FormField
                  label="Session Type"
                  value={data.sess_type}
                  editable={false}
                />
              )}
            </Grid>

            {/* Row 3: Session Start | Session End */}
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              <FormField
                label="Session Start"
                value={data.sess_start}
                editable={fieldEditable('sess_start')}
                onChange={(e) => onChange({ ...data, sess_start: toEasternISO(e.target.value) ?? null })}
                type="datetime-local"
                formatValue={formatDateTime}
                helperText={fieldEditable('sess_start') ? 'Enter the time in Eastern' : undefined}
              />
            </Grid>
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              <FormField
                label="Session End"
                value={data.sess_end}
                editable={fieldEditable('sess_end')}
                onChange={(e) => onChange({ ...data, sess_end: toEasternISO(e.target.value) ?? null })}
                type="datetime-local"
                formatValue={formatDateTime}
                helperText={fieldEditable('sess_end') ? 'Enter the time in Eastern' : undefined}
              />
            </Grid>

            {/* Row 4: Mode | Sample Type */}
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              {fieldEditable('mode') ? (
                <FormControl fullWidth error={!!errors.mode}>
                  <InputLabel sx={{ color: 'primary.main', fontWeight: 600 }}>Mode</InputLabel>
                  <Select
                    value={findMatchingValue(data.mode, surveyModes) || ''}
                    onChange={(e) => onChange({ ...data, mode: e.target.value })}
                    label="Mode"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {surveyModes.map((mode) => (
                      <MenuItem key={mode.name} value={mode.name}>
                        {mode.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.mode && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.mode}
                    </Typography>
                  )}
                </FormControl>
              ) : (
                <FormField
                  label="Mode"
                  value={data.mode}
                  editable={false}
                />
              )}
            </Grid>
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              <FormField
                label="Sample Type"
                value={data.sample_type}
                editable={fieldEditable('sample_type')}
                onChange={(e) => onChange({ ...data, sample_type: e.target.value })}
                error={errors.sample_type}
              />
            </Grid>

            {/* Row 5: Sample Geography | Sample N */}
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              <FormField
                label="Sample Geography"
                value={data.sample_geography}
                editable={fieldEditable('sample_geography')}
                onChange={(e) => onChange({ ...data, sample_geography: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              <FormField
                label="Sample N"
                value={data.sample_n}
                editable={fieldEditable('sample_n')}
                onChange={(e) => onChange({ ...data, sample_n: e.target.value })}
                type="number"
                error={errors.sample_n}
              />
            </Grid>

            {/* Row 6: Session Note (full width with dividers) */}
            <Grid item xs={12}>
              <Divider sx={{ my: editable ? 2 : 3 }} />
              <FormField
                label="Session Note"
                value={data.sess_notes}
                editable={fieldEditable('sess_notes')}
                onChange={(e) => onChange({ ...data, sess_notes: e.target.value })}
                multiline
                minRows={3}
                maxRows={10}
              />
              <Divider sx={{ mt: editable ? 3 : 4 }} />
            </Grid>

            {/* Row 7: Coded Type | Coded By */}
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              <FormField
                label="Coded Type"
                value={data.coded_type}
                editable={fieldEditable('coded_type')}
                onChange={(e) => onChange({ ...data, coded_type: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              <FormField
                label="Coded By"
                value={data.coded_by}
                editable={fieldEditable('coded_by')}
                onChange={(e) => onChange({ ...data, coded_by: e.target.value })}
              />
            </Grid>

            {/* Row 8: Code Notes (full width) */}
            <Grid item xs={12}>
              <FormField
                label="Code Notes"
                value={data.code_notes}
                editable={fieldEditable('code_notes')}
                onChange={(e) => onChange({ ...data, code_notes: e.target.value })}
                multiline
                minRows={3}
                maxRows={10}
              />
            </Grid>

            {/* ===== SURVEY DETAILS SECTION ===== */}
            <Grid item xs={12}>
              <Divider sx={{ my: editable ? 2 : 3 }} />
              <Typography variant="h4" sx={{ mb: 2 }}>
                Quantitative Details
              </Typography>
            </Grid>

            {/* Row 9: Issue Type | Survey Length */}
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              <FormField
                label="Issue Type"
                value={data.issue_type}
                editable={fieldEditable('issue_type')}
                onChange={(e) => onChange({ ...data, issue_type: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              <FormField
                label="Survey Length"
                value={data.survey_length}
                editable={fieldEditable('survey_length')}
                onChange={(e) => onChange({ ...data, survey_length: e.target.value })}
                type="number"
                error={errors.survey_length}
              />
            </Grid>

            {/* Row 10: Geo Type | VF Match */}
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              {fieldEditable('geo_type') ? (
                <FormControl fullWidth error={!!errors.geo_type}>
                  <InputLabel sx={{ color: 'primary.main', fontWeight: 600 }}>Geo Type</InputLabel>
                  <Select
                    value={findMatchingValue(data.geo_type, geographyTypes) || ''}
                    onChange={(e) => onChange({ ...data, geo_type: e.target.value })}
                    label="Geo Type"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {geographyTypes.map((geo) => (
                      <MenuItem key={geo.name} value={geo.name}>
                        {geo.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.geo_type && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.geo_type}
                    </Typography>
                  )}
                </FormControl>
              ) : (
                <FormField
                  label="Geo Type"
                  value={data.geo_type}
                  editable={false}
                />
              )}
            </Grid>
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              {fieldEditable('vf_match') ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.vf_match || false}
                      onChange={(e) => onChange({ ...data, vf_match: e.target.checked })}
                      size="small"
                    />
                  }
                  label="VF Match"
                  sx={{
                    mt: 1,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem',
                      color: 'primary.main',
                      fontWeight: 600,
                    }
                  }}
                />
              ) : (
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mb: 0.5 }}>
                    VF Match
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.primary' }}>
                    {data.vf_match ? 'Yes' : 'No'}
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* ===== FOCUS GROUP DETAILS SECTION ===== */}
            <Grid item xs={12}>
              <Divider sx={{ my: editable ? 2 : 3 }} />
              <Typography variant="h4" sx={{ mb: 2 }}>
                Qualitative Details
              </Typography>
            </Grid>

            {/* Row 11: FG Type | FG Geography */}
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              {fieldEditable('fg_type') ? (
                <FormControl fullWidth error={!!errors.fg_type}>
                  <InputLabel sx={{ color: 'primary.main', fontWeight: 600 }}>FG Type</InputLabel>
                  <Select
                    value={findMatchingValue(data.fg_type, fgTypes) || ''}
                    onChange={(e) => onChange({ ...data, fg_type: e.target.value })}
                    label="FG Type"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {fgTypes.map((type) => (
                      <MenuItem key={type.name} value={type.name}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.fg_type && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.fg_type}
                    </Typography>
                  )}
                </FormControl>
              ) : (
                <FormField
                  label="FG Type"
                  value={data.fg_type}
                  editable={false}
                />
              )}
            </Grid>
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              <FormField
                label="FG Geography"
                value={data.fg_geography}
                editable={fieldEditable('fg_geography')}
                onChange={(e) => onChange({ ...data, fg_geography: e.target.value })}
              />
            </Grid>

            {/* Row 12: FG Topic | Moderator */}
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              <FormField
                label="FG Topic"
                value={data.fg_topic}
                editable={fieldEditable('fg_topic')}
                onChange={(e) => onChange({ ...data, fg_topic: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              <FormField
                label="Moderator"
                value={data.moderator}
                editable={fieldEditable('moderator')}
                onChange={(e) => onChange({ ...data, moderator: e.target.value })}
              />
            </Grid>

            {/* Row 13: Recruited */}
            <Grid item xs={12} {...{ [gridBreakpoint]: 6 }}>
              <FormField
                label="Recruited"
                value={data.recruited}
                editable={fieldEditable('recruited')}
                onChange={(e) => onChange({ ...data, recruited: e.target.value })}
                type="number"
                error={errors.recruited}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

SessionFormFields.propTypes = {
  data: PropTypes.object.isRequired,
  editable: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  displayName: PropTypes.string,
  projectId: PropTypes.string,
  isCreating: PropTypes.bool,
};

export default SessionFormFields;
